import { useAtom } from 'jotai'
import * as qs from 'qs'
import { useCallback, useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { AppointmentType, SourceService } from '../../../__generated__/api'
import {
  APPOINTMENT_PARAMETERS,
  EXTERNAL_PARAMETERS,
  GLOBAL_PARAMETERS,
} from '../../../common/constants/urlParameters'
import useChangeLanguage from '../../../common/hooks/useChangeLanguage'
import {
  marketingCampaignCodeAtom,
  selectedAppointmentLengthAtom,
  selectedAppointmentNodeConnectionIdAtom,
  selectedAppointmentNodeIdAtom,
  selectedAppointmentServiceIdAtom,
  selectedAppointmentTypeAtom,
  sourceServiceAtom,
} from '../../../state/common/atoms'
import { initialReserveHistoryRestoredAtom } from '../../../state/reserve/atoms'
import { AppointmentSearchMode } from '../../../state/search/atoms'
import { useAppointmentSearchMode } from '../../../xstate/selectors'

const useReserveHistory = (): void => {
  const history = useHistory()

  const [selectedAppointmentType, setSelectedAppointmentType] = useAtom(selectedAppointmentTypeAtom)
  const [selectedAppointmentLength, setSelectedAppointmentLength] = useAtom(
    selectedAppointmentLengthAtom
  )

  const [selectedConnectionId, setSelectedConnectionId] = useAtom(
    selectedAppointmentNodeConnectionIdAtom
  )

  const [selectedNodeId, setSelectedNodeId] = useAtom(selectedAppointmentNodeIdAtom)

  const [selectedService, setSelectedService] = useAtom(selectedAppointmentServiceIdAtom)
  const [initialReserveHistoryRestored, setInitialReserveHistoryRestored] = useAtom(
    initialReserveHistoryRestoredAtom
  )

  const { currentLanguage } = useChangeLanguage()

  const appointmentSearchMode = useAppointmentSearchMode()

  const [marketingCampaignCode, setMarketingCampaignCode] = useAtom(marketingCampaignCodeAtom)

  const [selectedSourceService, setSelectedSourceService] = useAtom(sourceServiceAtom)

  // Deserialization fn to restore state from url
  const restoreFromHistory = useCallback(
    (searchString: string) => {
      if (searchString !== '') {
        const parsed = qs.parse(searchString)

        if (
          parsed[APPOINTMENT_PARAMETERS.LENGTH] &&
          typeof parsed[APPOINTMENT_PARAMETERS.LENGTH] === 'string'
        ) {
          if (parsed[APPOINTMENT_PARAMETERS.LENGTH] === 'default') {
            setSelectedAppointmentLength('default')
          } else {
            const parsedAppointmentLength = parseInt(
              parsed[APPOINTMENT_PARAMETERS.LENGTH] as string,
              10
            )
            if (!isNaN(parsedAppointmentLength)) {
              setSelectedAppointmentLength(parsedAppointmentLength)
            }
          }
        } else {
          setSelectedAppointmentLength('default')
        }

        if (
          parsed[APPOINTMENT_PARAMETERS.NODE] &&
          typeof parsed[APPOINTMENT_PARAMETERS.NODE] === 'string'
        ) {
          const parsedNode = parsed[APPOINTMENT_PARAMETERS.NODE] as string
          setSelectedNodeId(parsedNode)
        }

        if (
          parsed[APPOINTMENT_PARAMETERS.CONNECTION_ID] &&
          typeof parsed[APPOINTMENT_PARAMETERS.CONNECTION_ID] === 'string'
        ) {
          const parsedConn = parsed[APPOINTMENT_PARAMETERS.CONNECTION_ID] as string
          setSelectedConnectionId(parsedConn)
        }

        if (
          parsed[APPOINTMENT_PARAMETERS.SERVICE] &&
          typeof parsed[APPOINTMENT_PARAMETERS.SERVICE] === 'string' &&
          parsed[APPOINTMENT_PARAMETERS.SERVICE] !== 'undefined'
        ) {
          const parsedService = parsed[APPOINTMENT_PARAMETERS.SERVICE] as string
          setSelectedService(parseInt(parsedService, 10))
        }

        if (
          parsed[APPOINTMENT_PARAMETERS.MODE] &&
          typeof parsed[APPOINTMENT_PARAMETERS.MODE] === 'string'
        ) {
          const parsedMode = parsed[APPOINTMENT_PARAMETERS.MODE]
          if (parsedMode === AppointmentSearchMode.CALLBACK) {
            setSelectedAppointmentType(AppointmentType.Callback)
          } else {
            setSelectedAppointmentType(undefined)
          }
        }

        if (
          parsed[APPOINTMENT_PARAMETERS.TYPE] &&
          typeof parsed[APPOINTMENT_PARAMETERS.TYPE] === 'string'
        ) {
          const parsedAppointmentType = parsed[APPOINTMENT_PARAMETERS.TYPE] as string

          if (Object.values(AppointmentType).includes(parsedAppointmentType as AppointmentType)) {
            setSelectedAppointmentType(parsedAppointmentType as AppointmentType)
          }
        }

        if (
          parsed[EXTERNAL_PARAMETERS.MARKETING_CAMPAIGN_CODE] &&
          typeof parsed[EXTERNAL_PARAMETERS.MARKETING_CAMPAIGN_CODE] === 'string'
        ) {
          setMarketingCampaignCode(parsed[EXTERNAL_PARAMETERS.MARKETING_CAMPAIGN_CODE] as string)
        }

        //TODO: Remove session storage implementation when AV3-2652 is implemented
        if (
          parsed[GLOBAL_PARAMETERS.SOURCE_SERVICE] &&
          typeof parsed[GLOBAL_PARAMETERS.SOURCE_SERVICE] === 'string'
        ) {
          setSelectedSourceService(parsed[GLOBAL_PARAMETERS.SOURCE_SERVICE] as SourceService)
          // Store to session storage
          sessionStorage.setItem(
            'av3_sourceService',
            parsed[GLOBAL_PARAMETERS.SOURCE_SERVICE] as string
          )
        } else {
          //Try to fetch from session storage if not in url
          const sourceService = sessionStorage.getItem('av3_sourceService')
          if (sourceService) {
            setSelectedSourceService(sourceService as SourceService)
          }
        }
      }
      setInitialReserveHistoryRestored(true)
    },
    [
      setInitialReserveHistoryRestored,
      setSelectedAppointmentType,
      setSelectedAppointmentLength,
      setSelectedService,
      setSelectedNodeId,
      setSelectedConnectionId,
      setMarketingCampaignCode,
      setSelectedSourceService,
    ]
  )

  useEffect(() => {
    if (!initialReserveHistoryRestored) {
      restoreFromHistory(history.location.search.slice(1))
    }
    return history.listen((location, action) => {
      if (action !== 'REPLACE') {
        restoreFromHistory(history.location.search.slice(1))
      }
    })
  }, [restoreFromHistory, history, initialReserveHistoryRestored])

  // Serialize relevant state to url query string
  useEffect(() => {
    if (initialReserveHistoryRestored) {
      history.replace({
        search: qs.stringify({
          [APPOINTMENT_PARAMETERS.TYPE]: selectedAppointmentType,
          [APPOINTMENT_PARAMETERS.LENGTH]: selectedAppointmentLength,
          [APPOINTMENT_PARAMETERS.SERVICE]: selectedService || undefined,
          [GLOBAL_PARAMETERS.LANG]: currentLanguage,
          [APPOINTMENT_PARAMETERS.NODE]: selectedNodeId,
          [APPOINTMENT_PARAMETERS.CONNECTION_ID]: selectedConnectionId,
          [EXTERNAL_PARAMETERS.MARKETING_CAMPAIGN_CODE]: marketingCampaignCode,
          [GLOBAL_PARAMETERS.SOURCE_SERVICE]: selectedSourceService,
        }),
      })
    }
  }, [
    history,
    selectedAppointmentType,
    selectedAppointmentLength,
    currentLanguage,
    selectedService,
    selectedNodeId,
    selectedConnectionId,
    appointmentSearchMode,
    marketingCampaignCode,
    selectedSourceService,
    initialReserveHistoryRestored,
  ])
}

export default useReserveHistory
